'use client';

import { c as _c } from "react/compiler-runtime";
import { isPopupBlockedError, isSdkError, readXWaitlistFromUrl, stripXWaitlistQueryParams, useAuth, useMainnetOtpStart, useMainnetOtpVerify, useSdkTurnkey, useValidateEarlyAccess } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { type FormEvent, type ReactNode, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { EarlyAccessGateAnimationHandle } from '@/components/early-access-gate/early-access-gate-background';
import { EarlyAccessFooter, type EarlyAccessFormState, EarlyAccessGateForm, EarlyAccessGateShell, EarlyAccessModeSwitch, EarlyAccessOtpForm, EarlyAccessWaitlistForm, EarlyAccessWaitlistSuccess, EarlyAccessXEmailForm } from '@/components/early-access-gate/early-access-gate-ui';
import { EarlyAccessLeadModal, type LeadKind } from '@/components/early-access-gate/early-access-lead-modal';
import { clearQueueToken, getQueueToken, storeQueueToken } from '@/components/early-access-gate/early-access-queue';
import { captureReferralFromUrl, getStoredReferralCode } from '@/components/early-access-gate/early-access-referral';
import { EARLY_ACCESS_DATASET_ATTR, EARLY_ACCESS_DATASET_VALUE, getBetaAccessSnapshot, storeAccess, subscribeBetaAccess } from '@/components/early-access-gate/early-access-storage';
import { consumeWaitlistOtpHandoff } from '@/components/early-access-gate/waitlist-otp-handoff';
import { markRewardsUnlocked } from '@/components/rewards/rewards-unlocked';
import { DOCS_URL } from '@/config/docs-links';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { prefetchRouteFull } from '@/hooks/use-intent-route-prefetch';
import { clearWaitlistXAttributePending, markWaitlistXAttributePending } from '@/hooks/waitlist/use-attribute-waitlist-x-signup';
import { consumeNextAfterAuth, setNextAfterAuth } from '@/lib/auth/next-url';
import { stripDemoModePath, stripLeadingLocalePath } from '@/lib/network';
import { useAppRouter } from '@/lib/network/use-app-router';
import { logger } from '@/lib/telemetry/logger';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';
import { showToast } from '@/lib/ui/toast';
import { EMAIL_REGEX } from '@/lib/validation/email';
import { useEarlyAccessGateStore } from '@/stores/early-access-gate';
const PREVIEW_PATH = '/workspace/early-access-gate';
/** The dedicated early-access landing surface. A DEMO_MODE_EXCEPTION, so it
 *  always renders bare (never bounced into `/demo-mode/`). The gate ALWAYS
 *  fills this route — it never falls through to the page body. */
const EARLY_ACCESS_PATH = '/early-access';
/** Public editorial and legal pages must remain reachable before
 *  authentication. Mobile also opens the legal routes from its signed-out
 *  intro screen. */
const PUBLIC_PATHS: readonly string[] = ['/about', '/brand', '/delete-account', '/privacy', '/risk', '/terms'];
const PUBLIC_PATH_PREFIXES: readonly string[] = ['/compare', '/learn'];
/** Developer-only fixture surfaces own their access policy and must remain
 * reachable while the public early-access gate is enabled. */
const DEV_TOOL_PATHS: readonly string[] = ['/live-playground', '/funding/devtools', '/funding/fixtures'];
// Routes that carry their OWN gating and must pass through the early-access gate:
// `/rewards` (RewardsGate: flag + auth) and `/onboarding` (auth-only, the SDK
// sends a fresh account here before consuming the `/rewards` next-url). Without
// these, an account created via X — no access code, `devnet_enabled` OFF
// (the launch config) — gets bounced to the waitlist mid-flow. `/auth/callback`
// is the transient OAuth-return route (redirect mode); letting it through shows
// the real callback spinner instead of a flash of the waitlist card while the
// session mints.
const SELF_GATED_PATHS: readonly string[] = ['/rewards', '/onboarding', '/auth/callback'];
/** Where a granted user lands when no specific deep link was intercepted
 *  (`/markets` → `/demo-mode/markets` in demo mode). */
const APP_HOME = '/markets';
/** The site root. Pre-launch it serves the marketing landing page; the gate
 *  never covers or redirects it. */
const HOME_PATH = '/';

/** Customer.io's `cio_id` shape (lowercase hex). The launch email templates it
 *  onto the one-click grant link as `?cio=`; bounding it here keeps a mangled
 *  or hand-edited link from reaching the API at all. */
const CIO_PROFILE_REF_RE = /^[0-9a-f]{8,32}$/;

/**
 * The Customer.io profile the launch email addressed, when this visit came
 * from one. Passed to `early-access/validate` so a successful claim can be
 * attributed to the person who received the code — the automation branches on
 * that event to stop chasing people who already got in.
 *
 * Attribution only: it never affects whether access is granted.
 */
function readProfileRef(raw: string | null): string | undefined {
  const ref = raw?.trim();
  return ref !== undefined && CIO_PROFILE_REF_RE.test(ref) ? ref : undefined;
}

/** Bare (locale- and demo-mode-stripped) form of a path. */
function barePath(pathname: string): string {
  return stripLeadingLocalePath(stripDemoModePath(pathname));
}
function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.includes(pathname) || PUBLIC_PATH_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)) || DEV_TOOL_PATHS.includes(pathname);
}
function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Set when a waitlist "Continue with X" starts WITHOUT a rewards destination
// (rewards gate off). After auth resolves the gate reads this to land the user
// on the "You're on the waitlist" success card (mirroring the email OTP flow),
// instead of the bare signup chooser the active gate shows on /markets — which
// looks to the user like "nothing happened". sessionStorage so it survives the
// OAuth full-page redirect AND the window.open popup (copied to the popup at
// open, retained in the opener).
const X_WAITLIST_SUCCESS_KEY = 'morfi_x_waitlist_success_pending';
function markXWaitlistSuccessPending(): void {
  try {
    window.sessionStorage.setItem(X_WAITLIST_SUCCESS_KEY, '1');
  } catch {
    // SILENT-CATCH: sessionStorage blocked (privacy mode) — the success card is
    // a best-effort UX nicety; the account + waitlist row are still created.
  }
}
function consumeXWaitlistSuccessPending(): boolean {
  try {
    const pending = window.sessionStorage.getItem(X_WAITLIST_SUCCESS_KEY) === '1';
    if (pending) window.sessionStorage.removeItem(X_WAITLIST_SUCCESS_KEY);
    return pending;
  } catch {
    // SILENT-CATCH: sessionStorage throws in private mode / when storage is
    // disabled — treat as "no pending success" rather than surfacing an error.
    return false;
  }
}

/** Server-reported seconds-remaining off a 429's error details, when present. */
// ALLOW-UNKNOWN: caught error type from the SDK mutation — narrowed via isSdkError below.
function sdkRetryAfterSeconds(err: unknown): number | null {
  if (!isSdkError(err)) return null;
  // ALLOW-UNKNOWN: details is an untyped debug extra Object.assign'd onto the SdkError by throwHttpError; narrowed below.
  const details = (err as {
    details?: unknown;
  }).details;
  if (typeof details !== 'object' || details === null) return null;
  // ALLOW-UNKNOWN: narrowing object property access for the structural check below
  const v = (details as Record<string, unknown>).retryAfterSeconds;
  return typeof v === 'number' && v > 0 ? v : null;
}

/** Backend `error.code` (e.g. NOTIFICATION_OTP_INVALID) off a thrown SDK
 *  error — carried as the untyped `apiCode` debug extra, distinct from the
 *  transport-level `SdkError.code` union. */
// ALLOW-UNKNOWN: caught error type from the SDK mutation — narrowed via isSdkError below.
function sdkApiCode(err: unknown): string | null {
  if (!isSdkError(err)) return null;
  // ALLOW-UNKNOWN: apiCode is an untyped debug extra Object.assign'd onto the SdkError by throwHttpError; narrowed below.
  const apiCode = (err as {
    apiCode?: unknown;
  }).apiCode;
  return typeof apiCode === 'string' ? apiCode : null;
}
interface EarlyAccessGateProps {
  enabled: boolean;
  children: React.ReactNode;
}
export function EarlyAccessGate(t0) {
  const $ = _c(4);
  const {
    enabled,
    children
  } = t0;
  const pathname = usePathname();
  const path = barePath(pathname);
  if (!enabled || path === PREVIEW_PATH || isPublicPath(path)) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children) {
    t1 = <EarlyAccessGateActive>{children}</EarlyAccessGateActive>;
    $[2] = children;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

/** Which card the gate shows. `waitlist` is the landing state (signup v2:
 *  email-or-X chooser); `otp` verifies the email; `x-email` is the no-email
 *  X-join upgrade card. */
type GateCard = 'waitlist' | 'otp' | 'x-email' | 'waitlist-success' | 'code';
/** 60s resend cooldown mirrored client-side (server-enforced in Redis). */
const OTP_RESEND_COOLDOWN_MS = 60_000;
/** Code-form lifecycle. `celebrating` hides the card and plays the enter
 *  animation, which on completion grants access and enters the app. */
type CodeState = 'idle' | 'loading' | 'error' | 'celebrating';
function EarlyAccessGateActive(t0) {
  const $ = _c(184);
  const {
    children
  } = t0;
  const tWaitlist = useTranslations("landing.waitlist");
  const tOtp = useTranslations("landing.otp");
  const tXJoin = useTranslations("landing.xJoin");
  const tFooter = useTranslations("landing.footer");
  const locale = useLocale();
  const prefersReduced = useReducedMotion();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useAppRouter();
  const storedAccess = useSyncExternalStore(subscribeBetaAccess, getBetaAccessSnapshot, _temp);
  const devnetEnabled = useFeatureGate("devnet_enabled");
  const rewardsEnabled = useFeatureGate("rewards_platform");
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t1 = () => {
      if (barePath(pathname) !== EARLY_ACCESS_PATH) {
        return "waitlist";
      }
      const requestedCard = searchParams.get("card");
      if (requestedCard === "code") {
        return "code";
      }
      if (requestedCard === "otp") {
        return "otp";
      }
      return "waitlist";
    };
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [card, setCard] = useState(t1);
  const [codeState, setCodeState] = useState("idle");
  const [waitlistState, setWaitlistState] = useState("idle");
  const [waitlistError, setWaitlistError] = useState(undefined);
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const prefilledEmailRef = useRef(null);
  const [now, setNow] = useState(_temp2);
  const [leadModal, setLeadModal] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpState, setOtpState] = useState("idle");
  const [otpError, setOtpError] = useState(undefined);
  const [resendReadyAt, setResendReadyAt] = useState(null);
  const [xHandle, setXHandle] = useState(null);
  const [xJoinError, setXJoinError] = useState(undefined);
  const [verifiedEmail, setVerifiedEmail] = useState(null);
  const {
    isAuthenticated,
    isLoading: authLoading,
    user
  } = useAuth();
  const {
    handleXOauth
  } = useSdkTurnkey();
  const inputRef = useRef(null);
  const otpInputRef = useRef(null);
  const animationRef = useRef(null);
  const openedFiredRef = useRef(false);
  const cardRestoredRef = useRef(false);
  const xReturnConsumedRef = useRef(false);
  const accessCodeConsumedRef = useRef(false);
  const [codeGrantStatus, setCodeGrantStatus] = useState("idle");
  const xTokenRef = useRef(null);
  const prevAuthedRef = useRef(null);
  const validateEarlyAccess = useValidateEarlyAccess();
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      meta: {
        skipGlobalErrorHandler: true
      }
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    mutateAsync: otpStartAsync
  } = useMainnetOtpStart(t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      meta: {
        skipGlobalErrorHandler: true
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    mutateAsync: otpVerifyAsync
  } = useMainnetOtpVerify(t3);
  const hasAccess = storedAccess || devnetEnabled === true;
  const checking = !hasAccess && devnetEnabled === null;
  let t4;
  if ($[5] !== pathname) {
    t4 = barePath(pathname);
    $[5] = pathname;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onEarlyAccess = t4 === EARLY_ACCESS_PATH;
  let t5;
  if ($[7] !== pathname) {
    t5 = stripLeadingLocalePath(pathname);
    $[7] = pathname;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const onHome = t5 === HOME_PATH;
  let t6;
  if ($[9] !== pathname) {
    const bare = barePath(pathname);
    t6 = SELF_GATED_PATHS.some(p => bare === p || bare.startsWith(`${p}/`));
    $[9] = pathname;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const onSelfGatedRoute = t6;
  const gateOverlayShown = !onHome && !onSelfGatedRoute && !(hasAccess && !onEarlyAccess);
  const setGateOverlayVisible = useEarlyAccessGateStore(_temp3);
  let t7;
  let t8;
  if ($[11] !== gateOverlayShown || $[12] !== setGateOverlayVisible) {
    t7 = () => {
      setGateOverlayVisible(gateOverlayShown);
      return () => setGateOverlayVisible(false);
    };
    t8 = [gateOverlayShown, setGateOverlayVisible];
    $[11] = gateOverlayShown;
    $[12] = setGateOverlayVisible;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[15] !== rewardsEnabled) {
    t9 = () => {
      if (rewardsEnabled === true) {
        markRewardsUnlocked();
      }
    };
    t10 = [rewardsEnabled];
    $[15] = rewardsEnabled;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[18] !== authLoading || $[19] !== codeGrantStatus || $[20] !== hasAccess || $[21] !== isAuthenticated || $[22] !== onEarlyAccess || $[23] !== router) {
    t11 = () => {
      if (!hasAccess) {
        return;
      }
      if (codeGrantStatus !== "idle") {
        if (codeGrantStatus === "handled") {
          if (!onEarlyAccess) {
            setCodeGrantStatus("idle");
          }
          return;
        }
        if (authLoading) {
          return;
        }
        setCodeGrantStatus("handled");
        if (isAuthenticated) {
          router.replace("/onboarding");
        } else {
          if (onEarlyAccess) {
            router.replace(APP_HOME);
          }
        }
        return;
      }
      if (onEarlyAccess) {
        router.replace(APP_HOME);
      }
    };
    t12 = [authLoading, codeGrantStatus, hasAccess, isAuthenticated, onEarlyAccess, router];
    $[18] = authLoading;
    $[19] = codeGrantStatus;
    $[20] = hasAccess;
    $[21] = isAuthenticated;
    $[22] = onEarlyAccess;
    $[23] = router;
    $[24] = t11;
    $[25] = t12;
  } else {
    t11 = $[24];
    t12 = $[25];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[26] !== card || $[27] !== checking || $[28] !== hasAccess || $[29] !== locale || $[30] !== onHome || $[31] !== onSelfGatedRoute) {
    t13 = () => {
      if (!hasAccess && !checking && !onHome && !onSelfGatedRoute && card === "waitlist" && !openedFiredRef.current) {
        openedFiredRef.current = true;
        onEvent("early_access_waitlist_opened", {
          locale
        });
      }
    };
    t14 = [hasAccess, checking, onHome, onSelfGatedRoute, card, locale];
    $[26] = card;
    $[27] = checking;
    $[28] = hasAccess;
    $[29] = locale;
    $[30] = onHome;
    $[31] = onSelfGatedRoute;
    $[32] = t13;
    $[33] = t14;
  } else {
    t13 = $[32];
    t14 = $[33];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[34] !== checking || $[35] !== hasAccess || $[36] !== onEarlyAccess || $[37] !== onHome || $[38] !== onSelfGatedRoute) {
    t15 = () => {
      if (checking) {
        return;
      }
      const root = document.documentElement;
      if (onSelfGatedRoute || hasAccess && !onEarlyAccess && !onHome) {
        root.dataset[EARLY_ACCESS_DATASET_ATTR] = EARLY_ACCESS_DATASET_VALUE;
      } else {
        delete root.dataset[EARLY_ACCESS_DATASET_ATTR];
      }
    };
    t16 = [checking, hasAccess, onEarlyAccess, onHome, onSelfGatedRoute];
    $[34] = checking;
    $[35] = hasAccess;
    $[36] = onEarlyAccess;
    $[37] = onHome;
    $[38] = onSelfGatedRoute;
    $[39] = t15;
    $[40] = t16;
  } else {
    t15 = $[39];
    t16 = $[40];
  }
  useEffect(t15, t16);
  const cooldownTicking = card === "otp" && resendReadyAt !== null && resendReadyAt > now;
  let t17;
  let t18;
  if ($[41] !== cooldownTicking) {
    t17 = () => {
      if (!cooldownTicking) {
        return;
      }
      const id = window.setInterval(() => setNow(Date.now()), 1000);
      return () => window.clearInterval(id);
    };
    t18 = [cooldownTicking];
    $[41] = cooldownTicking;
    $[42] = t17;
    $[43] = t18;
  } else {
    t17 = $[42];
    t18 = $[43];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[44] !== pathname || $[45] !== searchParams) {
    t19 = () => {
      if (barePath(pathname) !== EARLY_ACCESS_PATH) {
        return;
      }
      const prefillEmail = searchParams.get("email")?.trim();
      if (prefillEmail && prefillEmail !== prefilledEmailRef.current) {
        prefilledEmailRef.current = prefillEmail;
        setEmail(prefillEmail);
      }
      const requestedCard_0 = searchParams.get("card");
      if (requestedCard_0 === "code") {
        setCard("code");
        setCodeState("idle");
        setWaitlistState("idle");
        setWaitlistError(undefined);
        return;
      }
      if (requestedCard_0 !== "otp") {
        return;
      }
      const handoff = consumeWaitlistOtpHandoff();
      if (handoff === null) {
        return;
      }
      setEmail(handoff.email);
      setResendReadyAt(handoff.resendReadyAt);
      setCard("otp");
    };
    t20 = [pathname, searchParams];
    $[44] = pathname;
    $[45] = searchParams;
    $[46] = t19;
    $[47] = t20;
  } else {
    t19 = $[46];
    t20 = $[47];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[48] !== searchParams || $[49] !== validateEarlyAccess) {
    t21 = () => {
      if (accessCodeConsumedRef.current) {
        return;
      }
      const urlCode = searchParams.get("access")?.trim();
      if (urlCode === undefined || urlCode.length === 0) {
        return;
      }
      accessCodeConsumedRef.current = true;
      const profileRef = readProfileRef(searchParams.get("cio"));
      validateEarlyAccess.mutateAsync({
        code: urlCode,
        ...(profileRef !== undefined ? {
          ref: profileRef
        } : {})
      }).then(res => {
        if ((res as {
          valid?: boolean;
        }).valid !== true) {
          return;
        }
        setCodeGrantStatus("pending");
        storeAccess();
        const url = new URL(window.location.href);
        url.searchParams.delete("access");
        url.searchParams.delete("cio");
        window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
      }).catch(_temp4);
    };
    t22 = [searchParams, validateEarlyAccess];
    $[48] = searchParams;
    $[49] = validateEarlyAccess;
    $[50] = t21;
    $[51] = t22;
  } else {
    t21 = $[50];
    t22 = $[51];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[52] !== locale || $[53] !== storedAccess || $[54] !== tWaitlist || $[55] !== tXJoin) {
    t23 = () => {
      if (xReturnConsumedRef.current) {
        return;
      }
      xReturnConsumedRef.current = true;
      captureReferralFromUrl(window.location.search);
      const result = readXWaitlistFromUrl(window.location.search);
      if (result === null) {
        return;
      }
      const ptToken = new URLSearchParams(window.location.search).get("pt");
      if (ptToken !== null && ptToken.length > 0) {
        storeQueueToken(ptToken, 0);
      }
      stripXWaitlistQueryParams();
      if (storedAccess) {
        return;
      }
      if (result.kind === "joined") {
        onEvent("waitlist_x_joined", {
          locale,
          hasEmail: true
        });
        setXHandle(result.handle.length > 0 ? result.handle : null);
        setVerifiedEmail(null);
        setCard("waitlist-success");
        showToast({
          id: TOAST_IDS.WAITLIST_SUBSCRIBED,
          message: tWaitlist("toast.subscribed"),
          variant: "success"
        });
        return;
      }
      if (result.kind === "need_email") {
        onEvent("waitlist_x_joined", {
          locale,
          hasEmail: false
        });
        setXHandle(result.handle.length > 0 ? result.handle : null);
        xTokenRef.current = result.xToken.length > 0 ? result.xToken : null;
        setCard("x-email");
        return;
      }
      setXJoinError(result.kind === "cancelled" ? tXJoin("cancelled") : tXJoin("failed"));
    };
    t24 = [locale, tWaitlist, tXJoin, storedAccess];
    $[52] = locale;
    $[53] = storedAccess;
    $[54] = tWaitlist;
    $[55] = tXJoin;
    $[56] = t23;
    $[57] = t24;
  } else {
    t23 = $[56];
    t24 = $[57];
  }
  useEffect(t23, t24);
  let t25;
  let t26;
  if ($[58] !== card || $[59] !== checking || $[60] !== hasAccess || $[61] !== searchParams) {
    t25 = () => {
      if (cardRestoredRef.current || checking) {
        return;
      }
      cardRestoredRef.current = true;
      if (hasAccess || card !== "waitlist") {
        return;
      }
      if (searchParams.get("card") === "code") {
        return;
      }
      if (new URLSearchParams(window.location.search).has("waitlist_x")) {
        return;
      }
      if (getQueueToken() !== null) {
        setCard("waitlist-success");
      }
    };
    t26 = [checking, hasAccess, card, searchParams];
    $[58] = card;
    $[59] = checking;
    $[60] = hasAccess;
    $[61] = searchParams;
    $[62] = t25;
    $[63] = t26;
  } else {
    t25 = $[62];
    t26 = $[63];
  }
  useEffect(t25, t26);
  let t27;
  let t28;
  if ($[64] !== authLoading || $[65] !== isAuthenticated) {
    t27 = () => {
      if (authLoading) {
        return;
      }
      const wasAuthed = prevAuthedRef.current;
      prevAuthedRef.current = isAuthenticated;
      if (wasAuthed === true && !isAuthenticated) {
        clearQueueToken();
        setVerifiedEmail(null);
        setXHandle(null);
        setEmail("");
        setCard("waitlist");
      }
    };
    t28 = [authLoading, isAuthenticated];
    $[64] = authLoading;
    $[65] = isAuthenticated;
    $[66] = t27;
    $[67] = t28;
  } else {
    t27 = $[66];
    t28 = $[67];
  }
  useEffect(t27, t28);
  let t29;
  if ($[68] !== authLoading || $[69] !== isAuthenticated || $[70] !== onEarlyAccess || $[71] !== user?.connectedProviders || $[72] !== user?.handle) {
    t29 = () => {
      if (authLoading || !isAuthenticated || !onEarlyAccess) {
        return;
      }
      if (!consumeXWaitlistSuccessPending()) {
        return;
      }
      setXHandle(user?.connectedProviders?.find(_temp5)?.username ?? user?.handle ?? null);
      setVerifiedEmail(null);
      setCard("x-email");
    };
    $[68] = authLoading;
    $[69] = isAuthenticated;
    $[70] = onEarlyAccess;
    $[71] = user?.connectedProviders;
    $[72] = user?.handle;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== authLoading || $[75] !== isAuthenticated || $[76] !== onEarlyAccess || $[77] !== user) {
    t30 = [authLoading, isAuthenticated, onEarlyAccess, user];
    $[74] = authLoading;
    $[75] = isAuthenticated;
    $[76] = onEarlyAccess;
    $[77] = user;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  useEffect(t29, t30);
  let t31;
  if ($[79] !== card || $[80] !== user?.connectedProviders || $[81] !== user?.handle || $[82] !== xHandle) {
    t31 = () => {
      if (card !== "x-email" || xHandle !== null) {
        return;
      }
      const handle = user?.connectedProviders?.find(_temp6)?.username ?? user?.handle ?? null;
      if (handle !== null) {
        setXHandle(handle);
      }
    };
    $[79] = card;
    $[80] = user?.connectedProviders;
    $[81] = user?.handle;
    $[82] = xHandle;
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  let t32;
  if ($[84] !== card || $[85] !== user || $[86] !== xHandle) {
    t32 = [card, xHandle, user];
    $[84] = card;
    $[85] = user;
    $[86] = xHandle;
    $[87] = t32;
  } else {
    t32 = $[87];
  }
  useEffect(t31, t32);
  let t33;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = function openCard(next) {
      setCard(next);
      setCodeState("idle");
      setWaitlistState("idle");
      setWaitlistError(undefined);
      xTokenRef.current = null;
    };
    $[88] = t33;
  } else {
    t33 = $[88];
  }
  const openCard = t33;
  let t34;
  if ($[89] !== locale) {
    t34 = function handleWaitlistVisible() {
      if (openedFiredRef.current) {
        return;
      }
      openedFiredRef.current = true;
      onEvent("early_access_waitlist_opened", {
        locale
      });
    };
    $[89] = locale;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  const handleWaitlistVisible = t34;
  let t35;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = function grantAndEnter() {
      setCodeGrantStatus("pending");
      storeAccess();
    };
    $[91] = t35;
  } else {
    t35 = $[91];
  }
  const grantAndEnter = t35;
  let t36;
  if ($[92] !== code || $[93] !== searchParams || $[94] !== validateEarlyAccess) {
    t36 = function submitCode(e) {
      e.preventDefault();
      setCodeState("loading");
      const profileRef_0 = readProfileRef(searchParams.get("cio"));
      validateEarlyAccess.mutateAsync({
        code: code.trim(),
        ...(profileRef_0 !== undefined ? {
          ref: profileRef_0
        } : {})
      }).then(res_0 => {
        if ((res_0 as {
          valid?: boolean;
        }).valid !== true) {
          setCodeState("error");
          setCode("");
          inputRef.current?.focus();
          return;
        }
        if (prefersReducedMotion()) {
          grantAndEnter();
          return;
        }
        setCodeState("celebrating");
        queueMicrotask(() => animationRef.current?.enter());
      }).catch(err_0 => {
        logger.warn("Early access validation failed", {
          error: err_0 instanceof Error ? err_0.message.slice(0, 200) : String(err_0)
        });
        setCodeState("error");
        setCode("");
        inputRef.current?.focus();
      });
    };
    $[92] = code;
    $[93] = searchParams;
    $[94] = validateEarlyAccess;
    $[95] = t36;
  } else {
    t36 = $[95];
  }
  const submitCode = t36;
  let t37;
  if ($[96] !== locale) {
    t37 = function buildOtpStartBody(trimmedEmail) {
      const referralCode = getStoredReferralCode();
      return {
        email: trimmedEmail,
        locale,
        source: "early_access_gate",
        consent: true,
        ...(referralCode !== null ? {
          referralCode
        } : {}),
        ...(xTokenRef.current !== null ? {
          xToken: xTokenRef.current
        } : {})
      };
    };
    $[96] = locale;
    $[97] = t37;
  } else {
    t37 = $[97];
  }
  const buildOtpStartBody = t37;
  let t38;
  if ($[98] !== buildOtpStartBody || $[99] !== card || $[100] !== email || $[101] !== locale || $[102] !== otpStartAsync || $[103] !== tOtp || $[104] !== tWaitlist) {
    t38 = function startOtp(opts) {
      const trimmed = email.trim();
      if (!EMAIL_REGEX.test(trimmed)) {
        setWaitlistError(tWaitlist("errors.invalid_email"));
        setWaitlistState("error");
        return;
      }
      setWaitlistState("loading");
      setWaitlistError(undefined);
      if (opts.resend) {
        setOtpState("loading");
      }
      otpStartAsync(buildOtpStartBody(trimmed)).then(() => {
        onEvent("waitlist_otp_sent", {
          locale,
          referralCode: getStoredReferralCode()
        });
        setOtp("");
        setOtpState("idle");
        setOtpError(undefined);
        setWaitlistState("idle");
        setNow(Date.now());
        setResendReadyAt(Date.now() + OTP_RESEND_COOLDOWN_MS);
        setCard("otp");
        if (opts.resend) {
          showToast({
            id: TOAST_IDS.WAITLIST_OTP_RESENT,
            message: tOtp("toast.resent", {
              email: trimmed
            }),
            variant: "success"
          });
        }
      }).catch(err_1 => {
        const sdkCode = sdkApiCode(err_1);
        if (sdkCode === "NOTIFICATION_OTP_COOLDOWN") {
          const retryAfter = sdkRetryAfterSeconds(err_1);
          setOtpState("idle");
          setWaitlistState("idle");
          setNow(Date.now());
          setResendReadyAt(Date.now() + (retryAfter ?? 60) * 1000);
          setCard("otp");
          return;
        }
        if (sdkCode === "NOTIFICATION_EMAIL_REJECTED") {
          onEvent("waitlist_otp_failed", {
            locale,
            reason: "invalid_email"
          });
          const rejectedMessage = tWaitlist("errors.rejected_email");
          showToast({
            id: TOAST_IDS.WAITLIST_EMAIL_REJECTED,
            message: rejectedMessage,
            variant: "error"
          });
          if (opts.resend || card === "otp") {
            setOtpState("error");
            setOtpError(rejectedMessage);
            setWaitlistState("idle");
          } else {
            setWaitlistError(rejectedMessage);
            setWaitlistState("error");
            setOtpState("idle");
          }
          return;
        }
        onEvent("waitlist_otp_failed", {
          locale,
          reason: sdkCode === "NOTIFICATION_OTP_SEND_LIMIT" || sdkCode === "NOTIFICATION_RATE_LIMITED" ? "rate_limited" : "server"
        });
        const message = sdkCode === "NOTIFICATION_OTP_SEND_LIMIT" ? tOtp("errors.sendLimit") : isSdkError(err_1) && err_1.category === "rate-limited" ? tWaitlist("errors.rate_limited") : tWaitlist("errors.server");
        if (opts.resend || card === "otp") {
          setOtpState("error");
          setOtpError(message);
          setWaitlistState("idle");
          return;
        }
        setWaitlistError(message);
        setWaitlistState("error");
      });
    };
    $[98] = buildOtpStartBody;
    $[99] = card;
    $[100] = email;
    $[101] = locale;
    $[102] = otpStartAsync;
    $[103] = tOtp;
    $[104] = tWaitlist;
    $[105] = t38;
  } else {
    t38 = $[105];
  }
  const startOtp = t38;
  let t39;
  if ($[106] !== startOtp) {
    t39 = function submitWaitlist(e_0) {
      e_0.preventDefault();
      startOtp({
        resend: false
      });
    };
    $[106] = startOtp;
    $[107] = t39;
  } else {
    t39 = $[107];
  }
  const submitWaitlist = t39;
  let t40;
  if ($[108] !== email || $[109] !== isAuthenticated || $[110] !== locale || $[111] !== otpState || $[112] !== otpVerifyAsync || $[113] !== tOtp || $[114] !== tWaitlist || $[115] !== user?.connectedProviders) {
    t40 = function verifyOtp(codeValue) {
      const trimmed_0 = email.trim();
      if (codeValue.length < 6 || otpState === "loading") {
        return;
      }
      setOtpState("loading");
      setOtpError(undefined);
      otpVerifyAsync({
        email: trimmed_0,
        code: codeValue
      }).then(res_1 => {
        const verifyData = res_1 as {
          token?: string;
          position?: number;
        };
        if (typeof verifyData.token === "string" && typeof verifyData.position === "number") {
          storeQueueToken(verifyData.token, verifyData.position);
        }
        const referralCode_0 = getStoredReferralCode();
        onEvent("waitlist_otp_verified", {
          locale,
          referralCode: referralCode_0
        });
        onEvent("waitlist_signup_complete", {
          source: "early_access_gate",
          email: trimmed_0,
          locale
        });
        showToast({
          id: TOAST_IDS.WAITLIST_SUBSCRIBED,
          message: tOtp("toast.verified"),
          variant: "success"
        });
        setVerifiedEmail(trimmed_0);
        const sessionHasX = isAuthenticated && (user?.connectedProviders?.some(_temp7) ?? false);
        if (xTokenRef.current === null && !sessionHasX) {
          setXHandle(null);
        }
        xTokenRef.current = null;
        setOtpState("idle");
        setCard("waitlist-success");
      }).catch(err_2 => {
        const sdkCode_0 = sdkApiCode(err_2);
        const reason = sdkCode_0 === "NOTIFICATION_OTP_INVALID" ? "invalid" : sdkCode_0 === "NOTIFICATION_OTP_EXPIRED" ? "expired" : sdkCode_0 === "NOTIFICATION_OTP_TOO_MANY_ATTEMPTS" ? "too_many_attempts" : isSdkError(err_2) && err_2.category === "rate-limited" ? "rate_limited" : "server";
        onEvent("waitlist_otp_failed", {
          locale,
          reason
        });
        const message_0 = reason === "invalid" ? tOtp("errors.invalid") : reason === "expired" ? tOtp("errors.expired") : reason === "too_many_attempts" ? tOtp("errors.tooMany") : reason === "rate_limited" ? tWaitlist("errors.rate_limited") : tWaitlist("errors.server");
        setOtp("");
        setOtpState("error");
        setOtpError(message_0);
        otpInputRef.current?.focus();
      });
    };
    $[108] = email;
    $[109] = isAuthenticated;
    $[110] = locale;
    $[111] = otpState;
    $[112] = otpVerifyAsync;
    $[113] = tOtp;
    $[114] = tWaitlist;
    $[115] = user?.connectedProviders;
    $[116] = t40;
  } else {
    t40 = $[116];
  }
  const verifyOtp = t40;
  let t41;
  if ($[117] !== otp || $[118] !== verifyOtp) {
    t41 = function submitOtp(e_1) {
      e_1.preventDefault();
      verifyOtp(otp);
    };
    $[117] = otp;
    $[118] = verifyOtp;
    $[119] = t41;
  } else {
    t41 = $[119];
  }
  const submitOtp = t41;
  let t42;
  if ($[120] !== handleXOauth || $[121] !== router) {
    t42 = function startXAccountOauth(goToRewards) {
      markWaitlistXAttributePending();
      if (goToRewards) {
        setNextAfterAuth("/rewards");
        prefetchRouteFull(router, "/rewards");
      } else {
        setNextAfterAuth("/early-access");
        markXWaitlistSuccessPending();
      }
      const undoStaged = () => {
        clearWaitlistXAttributePending();
        consumeNextAfterAuth();
        if (!goToRewards) {
          consumeXWaitlistSuccessPending();
        }
      };
      handleXOauth().catch(err_3 => {
        if (isPopupBlockedError(err_3)) {
          handleXOauth({
            openInPage: true
          }).catch(undoStaged);
          return;
        }
        undoStaged();
      });
    };
    $[120] = handleXOauth;
    $[121] = router;
    $[122] = t42;
  } else {
    t42 = $[122];
  }
  const startXAccountOauth = t42;
  let t43;
  if ($[123] !== isAuthenticated || $[124] !== locale || $[125] !== startXAccountOauth || $[126] !== user?.handle) {
    t43 = function continueWithX() {
      onEvent("waitlist_x_started", {
        locale,
        referralCode: getStoredReferralCode()
      });
      setXJoinError(undefined);
      if (isAuthenticated) {
        setXHandle(user?.handle ?? null);
        setVerifiedEmail(null);
        setCard("waitlist-success");
        return;
      }
      startXAccountOauth(false);
    };
    $[123] = isAuthenticated;
    $[124] = locale;
    $[125] = startXAccountOauth;
    $[126] = user?.handle;
    $[127] = t43;
  } else {
    t43 = $[127];
  }
  const continueWithX = t43;
  let t44;
  if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = function skipXEmail() {
      xTokenRef.current = null;
      setCard("waitlist-success");
    };
    $[128] = t44;
  } else {
    t44 = $[128];
  }
  const skipXEmail = t44;
  let t45;
  if ($[129] !== isAuthenticated || $[130] !== router || $[131] !== startXAccountOauth) {
    t45 = function handleStartQuesting() {
      onEvent("rewards_start_questing_clicked", {
        authed: isAuthenticated
      });
      if (isAuthenticated) {
        router.push("/rewards");
        return;
      }
      startXAccountOauth(true);
    };
    $[129] = isAuthenticated;
    $[130] = router;
    $[131] = startXAccountOauth;
    $[132] = t45;
  } else {
    t45 = $[132];
  }
  const handleStartQuesting = t45;
  if (codeGrantStatus === "pending") {
    return null;
  }
  if (onHome) {
    return children;
  }
  if (onSelfGatedRoute) {
    return children;
  }
  if (checking) {
    let t46;
    if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
      t46 = <EarlyAccessGateShell showAnimation={false}>{null}</EarlyAccessGateShell>;
      $[133] = t46;
    } else {
      t46 = $[133];
    }
    return t46;
  }
  if (hasAccess && !onEarlyAccess) {
    let t46;
    if ($[134] !== children) {
      t46 = <>{children}</>;
      $[134] = children;
      $[135] = t46;
    } else {
      t46 = $[135];
    }
    return t46;
  }
  const codeFormState = codeState === "loading" ? "loading" : codeState === "error" ? "error" : "idle";
  const redirectingHome = devnetEnabled === true && onEarlyAccess;
  if (redirectingHome) {
    let t46;
    if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
      t46 = <EarlyAccessGateShell showAnimation={false}>{null}</EarlyAccessGateShell>;
      $[136] = t46;
    } else {
      t46 = $[136];
    }
    return t46;
  }
  const visibleCard = codeState === "celebrating" ? null : card;
  const t46 = !checking;
  let t47;
  let t48;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = () => setLeadModal("partner");
    t48 = () => setLeadModal("invest");
    $[137] = t47;
    $[138] = t48;
  } else {
    t47 = $[137];
    t48 = $[138];
  }
  let t49;
  if ($[139] !== tFooter) {
    t49 = tFooter("helpUsGrow");
    $[139] = tFooter;
    $[140] = t49;
  } else {
    t49 = $[140];
  }
  let t50;
  if ($[141] !== tFooter) {
    t50 = tFooter("partner");
    $[141] = tFooter;
    $[142] = t50;
  } else {
    t50 = $[142];
  }
  let t51;
  if ($[143] !== tFooter) {
    t51 = tFooter("invest");
    $[143] = tFooter;
    $[144] = t51;
  } else {
    t51 = $[144];
  }
  let t52;
  if ($[145] !== t49 || $[146] !== t50 || $[147] !== t51) {
    t52 = <EarlyAccessFooter followHref={`${DOCS_URL}/resources/help-us-grow`} onPartner={t47} onInvest={t48} followLabel={t49} partnerLabel={t50} investLabel={t51} />;
    $[145] = t49;
    $[146] = t50;
    $[147] = t51;
    $[148] = t52;
  } else {
    t52 = $[148];
  }
  const t53 = resendReadyAt !== null ? Math.max(0, Math.ceil((resendReadyAt - now) / 1000)) : 0;
  let t54;
  if ($[149] !== startOtp) {
    t54 = () => startOtp({
      resend: true
    });
    $[149] = startOtp;
    $[150] = t54;
  } else {
    t54 = $[150];
  }
  const t55 = rewardsEnabled === true && !authLoading ? handleStartQuesting : undefined;
  let t56;
  if ($[151] !== code || $[152] !== codeFormState || $[153] !== continueWithX || $[154] !== email || $[155] !== handleWaitlistVisible || $[156] !== isAuthenticated || $[157] !== otp || $[158] !== otpError || $[159] !== otpState || $[160] !== prefersReduced || $[161] !== submitCode || $[162] !== submitOtp || $[163] !== submitWaitlist || $[164] !== t53 || $[165] !== t54 || $[166] !== t55 || $[167] !== verifiedEmail || $[168] !== verifyOtp || $[169] !== visibleCard || $[170] !== waitlistError || $[171] !== waitlistState || $[172] !== xHandle || $[173] !== xJoinError) {
    t56 = <GateCardSwitch visibleCard={visibleCard} prefersReduced={prefersReduced} onWaitlistVisible={handleWaitlistVisible} email={email} onEmailChange={setEmail} onSubmitWaitlist={submitWaitlist} waitlistState={waitlistState} waitlistError={waitlistError} onContinueWithX={continueWithX} xJoinError={xJoinError} otp={otp} onOtpChange={setOtp} onOtpComplete={verifyOtp} onSubmitOtp={submitOtp} otpState={otpState} otpError={otpError} resendSecondsLeft={t53} otpInputRef={otpInputRef} onResendOtp={t54} xHandle={xHandle} onSkipXEmail={skipXEmail} verifiedEmail={verifiedEmail} code={code} onCodeChange={setCode} onSubmitCode={submitCode} codeFormState={codeFormState} inputRef={inputRef} onOpenCard={openCard} onStartQuesting={t55} questingAuthed={isAuthenticated} />;
    $[151] = code;
    $[152] = codeFormState;
    $[153] = continueWithX;
    $[154] = email;
    $[155] = handleWaitlistVisible;
    $[156] = isAuthenticated;
    $[157] = otp;
    $[158] = otpError;
    $[159] = otpState;
    $[160] = prefersReduced;
    $[161] = submitCode;
    $[162] = submitOtp;
    $[163] = submitWaitlist;
    $[164] = t53;
    $[165] = t54;
    $[166] = t55;
    $[167] = verifiedEmail;
    $[168] = verifyOtp;
    $[169] = visibleCard;
    $[170] = waitlistError;
    $[171] = waitlistState;
    $[172] = xHandle;
    $[173] = xJoinError;
    $[174] = t56;
  } else {
    t56 = $[174];
  }
  let t57;
  if ($[175] !== t46 || $[176] !== t52 || $[177] !== t56) {
    t57 = <EarlyAccessGateShell showAnimation={t46} animationRef={animationRef} onRevealComplete={grantAndEnter} footer={t52}>{t56}</EarlyAccessGateShell>;
    $[175] = t46;
    $[176] = t52;
    $[177] = t56;
    $[178] = t57;
  } else {
    t57 = $[178];
  }
  let t58;
  if ($[179] !== leadModal) {
    t58 = leadModal !== null ? <EarlyAccessLeadModal kind={leadModal} onClose={() => setLeadModal(null)} /> : null;
    $[179] = leadModal;
    $[180] = t58;
  } else {
    t58 = $[180];
  }
  let t59;
  if ($[181] !== t57 || $[182] !== t58) {
    t59 = <>{t57}{t58}</>;
    $[181] = t57;
    $[182] = t58;
    $[183] = t59;
  } else {
    t59 = $[183];
  }
  return t59;
}
function _temp7(p_2) {
  return p_2.provider === "x";
}
function _temp6(p_1) {
  return p_1.provider === "x";
}
function _temp5(p_0) {
  return p_0.provider === "x";
}
function _temp4(err) {
  logger.warn("Early access URL-code validation failed", {
    error: err instanceof Error ? err.message.slice(0, 200) : String(err)
  });
}
function _temp3(s) {
  return s.setOverlayVisible;
}
function _temp2() {
  return Date.now();
}
function _temp() {
  return false;
}
interface GateCardSwitchProps {
  visibleCard: GateCard | null;
  prefersReduced: boolean | null;
  onWaitlistVisible: () => void;
  email: string;
  onEmailChange: (value: string) => void;
  onSubmitWaitlist: (e: FormEvent<HTMLFormElement>) => void;
  waitlistState: EarlyAccessFormState;
  waitlistError: string | undefined;
  onContinueWithX: () => void;
  xJoinError: string | undefined;
  otp: string;
  onOtpChange: (value: string) => void;
  onOtpComplete: (value: string) => void;
  onSubmitOtp: (e: FormEvent<HTMLFormElement>) => void;
  otpState: EarlyAccessFormState;
  otpError: string | undefined;
  resendSecondsLeft: number;
  otpInputRef: React.RefObject<HTMLInputElement | null>;
  onResendOtp: () => void;
  xHandle: string | null;
  onSkipXEmail: () => void;
  verifiedEmail: string | null;
  code: string;
  onCodeChange: (value: string) => void;
  onSubmitCode: (e: FormEvent<HTMLFormElement>) => void;
  codeFormState: EarlyAccessFormState;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onOpenCard: (next: GateCard) => void;
  /** "Start questing" handler — present only when the rewards gate is live. */
  onStartQuesting: (() => void) | undefined;
  /** True when the viewer already has a Morfi account (signed up with X). */
  questingAuthed: boolean;
}

/** The animated card switch inside the gate shell — purely presentational over
 *  the already-resolved `visibleCard` and the per-card data/handlers. */
function GateCardSwitch(t0) {
  const $ = _c(38);
  const {
    visibleCard,
    prefersReduced,
    onWaitlistVisible,
    email,
    onEmailChange,
    onSubmitWaitlist,
    waitlistState,
    waitlistError,
    onContinueWithX,
    xJoinError,
    otp,
    onOtpChange,
    onOtpComplete,
    onSubmitOtp,
    otpState,
    otpError,
    resendSecondsLeft,
    otpInputRef,
    onResendOtp,
    xHandle,
    onSkipXEmail,
    verifiedEmail,
    code,
    onCodeChange,
    onSubmitCode,
    codeFormState,
    inputRef,
    onOpenCard,
    onStartQuesting,
    questingAuthed
  } = t0;
  const t = useTranslations("landing");
  const tWaitlist = useTranslations("landing.waitlist");
  let t1;
  if ($[0] !== onWaitlistVisible || $[1] !== visibleCard) {
    t1 = visibleCard === "waitlist" ? node => {
      if (node !== null) {
        onWaitlistVisible();
      }
    } : undefined;
    $[0] = onWaitlistVisible;
    $[1] = visibleCard;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const cardRef = t1;
  let t2;
  if ($[3] !== cardRef || $[4] !== code || $[5] !== codeFormState || $[6] !== email || $[7] !== inputRef || $[8] !== onCodeChange || $[9] !== onContinueWithX || $[10] !== onEmailChange || $[11] !== onOpenCard || $[12] !== onOtpChange || $[13] !== onOtpComplete || $[14] !== onResendOtp || $[15] !== onSkipXEmail || $[16] !== onStartQuesting || $[17] !== onSubmitCode || $[18] !== onSubmitOtp || $[19] !== onSubmitWaitlist || $[20] !== otp || $[21] !== otpError || $[22] !== otpInputRef || $[23] !== otpState || $[24] !== prefersReduced || $[25] !== questingAuthed || $[26] !== resendSecondsLeft || $[27] !== t || $[28] !== tWaitlist || $[29] !== verifiedEmail || $[30] !== visibleCard || $[31] !== waitlistError || $[32] !== waitlistState || $[33] !== xHandle || $[34] !== xJoinError) {
    t2 = visibleCard !== null ? <m.div ref={cardRef} key={visibleCard} data-testid="gate-card-anim" initial={prefersReduced ? false : {
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={prefersReduced ? {
      opacity: 0
    } : {
      opacity: 0,
      y: -6
    }} transition={prefersReduced ? {
      duration: 0
    } : MOTION_ENTRANCE} className="w-full">{visibleCard === "waitlist" ? <EarlyAccessWaitlistForm email={email} onEmailChange={onEmailChange} onSubmit={onSubmitWaitlist} state={waitlistState} errorMessage={waitlistError} onSwitchToCode={() => onOpenCard("code")} onContinueWithX={onContinueWithX} xErrorMessage={xJoinError} /> : visibleCard === "otp" ? <EarlyAccessOtpForm email={email.trim()} otp={otp} onOtpChange={onOtpChange} onOtpComplete={onOtpComplete} onVerify={onSubmitOtp} state={otpState} errorMessage={otpError} resendSecondsLeft={resendSecondsLeft} inputRef={otpInputRef} onResend={onResendOtp} onChangeEmail={() => onOpenCard("waitlist")} /> : visibleCard === "x-email" ? <EarlyAccessXEmailForm xHandle={xHandle ?? ""} email={email} onEmailChange={onEmailChange} onSubmit={onSubmitWaitlist} state={waitlistState} errorMessage={waitlistError} onSkip={onSkipXEmail} /> : visibleCard === "waitlist-success" ? <EarlyAccessWaitlistSuccess onSwitchToCode={() => onOpenCard("code")} onJoinAnother={() => onOpenCard("waitlist")} verifiedEmail={verifiedEmail} xHandle={xHandle} onStartQuesting={onStartQuesting} questingAuthed={questingAuthed} /> : <EarlyAccessGateForm subtitle={t("subtitle")} code={code} onCodeChange={onCodeChange} onSubmit={onSubmitCode} state={codeFormState} inputRef={inputRef} label={t("accessCodeLabel")} placeholder={t("accessCodePlaceholder")} submitLabel={t("enterApp")} loadingLabel={t("verifyingCode")} footer={<EarlyAccessModeSwitch label={tWaitlist("switchToWaitlist")} onClick={() => onOpenCard("waitlist")} />} />}</m.div> : null;
    $[3] = cardRef;
    $[4] = code;
    $[5] = codeFormState;
    $[6] = email;
    $[7] = inputRef;
    $[8] = onCodeChange;
    $[9] = onContinueWithX;
    $[10] = onEmailChange;
    $[11] = onOpenCard;
    $[12] = onOtpChange;
    $[13] = onOtpComplete;
    $[14] = onResendOtp;
    $[15] = onSkipXEmail;
    $[16] = onStartQuesting;
    $[17] = onSubmitCode;
    $[18] = onSubmitOtp;
    $[19] = onSubmitWaitlist;
    $[20] = otp;
    $[21] = otpError;
    $[22] = otpInputRef;
    $[23] = otpState;
    $[24] = prefersReduced;
    $[25] = questingAuthed;
    $[26] = resendSecondsLeft;
    $[27] = t;
    $[28] = tWaitlist;
    $[29] = verifiedEmail;
    $[30] = visibleCard;
    $[31] = waitlistError;
    $[32] = waitlistState;
    $[33] = xHandle;
    $[34] = xJoinError;
    $[35] = t2;
  } else {
    t2 = $[35];
  }
  let t3;
  if ($[36] !== t2) {
    t3 = <LazyMotion features={domAnimation} strict={true}><AnimatePresence mode="wait" initial={false}>{t2}</AnimatePresence></LazyMotion>;
    $[36] = t2;
    $[37] = t3;
  } else {
    t3 = $[37];
  }
  return t3;
}